import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationOnIcon,
  Share as ShareIcon,
} from "@mui/icons-material";
import { useAuth } from "../../contexts/AuthContext";
import ShareFeedbackModal from "../../components/ShareFeedbackModal";

const BusinessDetails = () => {
  const { user } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [shareableLink, setShareableLink] = useState("");

  const handleShareFeedbackLink = () => {
    const frontendBaseURL = window.location.origin; // Dynamically fetch base URL
    setShareableLink(`${frontendBaseURL}/feedback/${user.business_token}`);
    setIsModalOpen(true);
  };

  if (!user) {
    return (
      <Box
        sx={{
          p: 4,
          minHeight: "calc(100vh - 60px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="h6" color="text.secondary">
          Loading user details...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3 },
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      {/* Header */}
      <Box>
        <Typography
          variant="h4"
          sx={{
            fontWeight: "bold",
            mb: 1.5,
            color: "primary.main",
            textAlign: { xs: "center", sm: "left" },
          }}
        >
          Business Details
        </Typography>
        <Typography
          variant="subtitle2"
          color="text.secondary"
          sx={{ mb: 2, textAlign: { xs: "center", sm: "left" } }}
        >
          View your business details and share the feedback link using a URL or QR code.
        </Typography>
        <Divider />
      </Box>

      {/* Business Details List */}
      <List sx={{ mt: 2 }}>
        {[
          { label: "Name", value: user.name, icon: <PersonIcon color="primary" /> },
          { label: "Email", value: user.email, icon: <EmailIcon color="primary" /> },
          { label: "Phone", value: user.phone || "N/A", icon: <PhoneIcon color="primary" /> },
          { label: "Address", value: user.address || "N/A", icon: <LocationOnIcon color="primary" /> },
        ].map((item, index) => (
          <React.Fragment key={index}>
            <ListItem
              sx={{
                py: 1, // Reduced padding
                display: "flex",
                alignItems: "flex-start",
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
              <ListItemText
                primary={
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      color: "#333",
                    }}
                  >
                    {item.label}
                  </Typography>
                }
                secondary={
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#555",
                    }}
                  >
                    {item.value}
                  </Typography>
                }
              />
            </ListItem>
            {index < 3 && <Divider component="li" />}
          </React.Fragment>
        ))}
      </List>

      {/* Share Button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mt: 3,
        }}
      >
        <Button
          variant="contained"
          startIcon={<ShareIcon />}
          color="primary"
          onClick={handleShareFeedbackLink}
          sx={{
            px: 3,
            py: 1,
            fontWeight: "bold",
            fontSize: "1rem",
            borderRadius: "8px",
            textTransform: "none",
          }}
        >
          Share Feedback Link
        </Button>
      </Box>

      {/* Share Feedback Modal */}
      <ShareFeedbackModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        businessName={user.name}
        shareableLink={shareableLink}
      />
    </Box>
  );
};

export default BusinessDetails;
